import React, { useMemo, useRef, useState } from "react";

import {
  faBox,
  faCube,
  faMagnifyingGlass,
  faPlus,
  faWarning,
} from "@fortawesome/pro-regular-svg-icons";
import { faPlay } from "@fortawesome/pro-solid-svg-icons";
import { isEqual } from "lodash-es";
import { FormProvider, useForm } from "react-hook-form";

import { FlowVersion } from "src/api/flowTypes";
import { Button } from "src/base-components/Button";
import { EditorBoundsProvider } from "src/base-components/CodeInput/useEditorBounds";
import { SplitButton } from "src/base-components/SplitButton";
import { TitleEditor } from "src/base-components/TitleEditor";
import {
  DecisionEnvironment,
  EntitiesSourcePreviewRequestFilterCondition,
  EntityJobSourceConfigurationInput,
  EntityJobSourceConfigurationInputTypeEnum,
  EntityJobSourceConfigurationOutputTypeEnum,
} from "src/clients/jobs";
import { Accordion } from "src/design-system/Accordion";
import { Callout } from "src/design-system/Callout";
import { EmptyState } from "src/design-system/EmptyState";
import { Modal, useModal } from "src/design-system/Modal";
import { TAKTILE_TEAM_NOTIFIED } from "src/design-system/Toast/constants";
import { toastActions } from "src/design-system/Toast/utils";
import { EntitySchemaResource, useEntitySchemas } from "src/entities/queries";
import { useFeatures } from "src/featureCatalogue/queries";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { useFlowVersions, usePatchJobSource } from "src/jobs/api/queries";
import { EntitiesPreview } from "src/jobs/common/entitySource/EntitiesPreview";
import { EntitiesPreviewSkeleton } from "src/jobs/common/entitySource/EntitiesPreviewSkeleton";
import { EntityTypeSelector } from "src/jobs/common/entitySource/EntityTypeSelector";
import { FilterConditionsAccordion } from "src/jobs/common/entitySource/FilterConditionsAccordion";
import { FilterConditionsAccordionSkeleton } from "src/jobs/common/entitySource/FilterConditionsAccordionSkeleton";
import {
  UseEntitySourcePreviewCountMutationArgs,
  UseEntitySourcePreviewMutationArgs,
  useEntitySourcePreview,
  useEntitySourcePreviewCount,
} from "src/jobs/common/entitySource/queries";
import { SortAndLimitAccordion } from "src/jobs/common/entitySource/SortAndLimitAccordion";
import { FieldErrors, FormData } from "src/jobs/common/entitySource/types";
import { buildDefaultValues } from "src/jobs/common/entitySource/utils";
import { useJobSource } from "src/jobs/common/hooks";
import { TrafficPolicyModal } from "src/jobs/common/TrafficPolicyModal";
import { Job, JobEntitySource } from "src/jobs/types";
import { queryClient } from "src/queryClient";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import {
  decodeJsonRefPath,
  isJsonRefPath,
  isReferencingEntity,
} from "src/schema/jsonRefUtils";
import { logger } from "src/utils/logger";
import { isPreconditionError } from "src/utils/predicates";
import { errorMessage } from "src/utils/stringUtils";

type EntitySourceUpdates = {
  name?: string;
  configuration?: Partial<EntityJobSourceConfigurationInput>;
};

type Props = {
  open: boolean;
  versions: FlowVersion[];
  job: Job;
  sourceId: string | undefined;
  afterLeave?: () => void;
  onClose: () => void;
};

export const isValidVersions = (versions: FlowVersion[]) => {
  if (versions.length === 0) {
    return false;
  }

  const inputSchemaFields = versions.flatMap((version) =>
    Object.entries(version.input_schema?.properties ?? {}),
  );

  const entityFields = inputSchemaFields.filter(
    ([, field]) => isJsonRefPath(field.$ref) && isReferencingEntity(field.$ref),
  );
  const featuresFields = inputSchemaFields.filter(
    ([fieldName, field]) =>
      fieldName === "features" && isEqual(field.type, ["object", "null"]),
  );

  const [firstEntityFieldName, firstEntityField] = entityFields[0] ?? [];

  return (
    // makes sure there are no extra fields except entity and features
    inputSchemaFields.length > 0 &&
    entityFields.length + featuresFields.length === inputSchemaFields.length &&
    entityFields.every(
      ([fieldName, field]) =>
        fieldName === firstEntityFieldName &&
        field.$ref === firstEntityField.$ref,
    )
  );
};

const parseFieldErrors = (error: unknown) => {
  if (typeof error === "object" && error !== null) {
    if ("detail" in error && Array.isArray(error.detail)) {
      return error.detail.reduce((acc, curr) => {
        if (
          typeof curr === "object" &&
          "field_id" in curr &&
          "message" in curr
        ) {
          acc[curr.field_id] = curr.message;
        }
        return acc;
      }, {} as FieldErrors);
    }
  }

  return undefined;
};

export const EntitySourceModal: React.FC<Props> = ({
  open,
  sourceId,
  onClose,
  afterLeave,
  versions,
  job,
}) => {
  const { workspace } = useWorkspaceContext();
  const source = useJobSource<JobEntitySource>(job.id, sourceId);
  const { data: entitySchemas } = useEntitySchemas({
    baseUrl: workspace.base_url!,
  });

  const { mutateAsync: updateSource, isPending: isUpdatingSource } =
    usePatchJobSource(workspace.base_url!, job.id);
  const { isOpen, openModal: openTrafficPolicyModal, closeModal } = useModal();

  const handleUpdateSource = async (updates: EntitySourceUpdates) => {
    if (!source || !sourceId) return;

    try {
      await updateSource({
        id: source.id,
        etag: source.etag,
        name: updates.name ?? source.name,
        configuration: {
          ...source.configuration,
          ...updates.configuration,
          type: EntityJobSourceConfigurationOutputTypeEnum.ENTITY,
        },
      });
      onClose();
    } catch (error) {
      logger.error(error);

      if (isPreconditionError(error)) {
        await queryClient.invalidateQueries({
          queryKey: ["job_sources", workspace.base_url, job.id],
        });
        toastActions.failure({
          title: "Failed to update Entity source",
          description:
            "Somebody has changed the Source, check the updated configuration and try again",
        });
      } else {
        toastActions.failure({
          title: "Failed to update Entity source",
          description: TAKTILE_TEAM_NOTIFIED,
        });
      }
    }
  };

  return (
    <>
      <Modal
        afterLeave={afterLeave}
        closeMode="persistent"
        open={open}
        size="xl"
        onClose={onClose}
      >
        <Modal.Header>
          <TitleEditor
            placeholder="Data source name"
            value={source?.name}
            onSubmit={(name) => handleUpdateSource({ name })}
          />
        </Modal.Header>
        {source && entitySchemas?.entities && (
          <ModalContent
            entitySchemas={entitySchemas.entities}
            isUpdatingSource={isUpdatingSource}
            jobId={job.id}
            source={source}
            versions={versions}
            onOpenTrafficPolicyModal={openTrafficPolicyModal}
            onSubmit={handleUpdateSource}
          />
        )}
      </Modal>
      <TrafficPolicyModal job={job} open={isOpen} onClose={closeModal} />
    </>
  );
};

type ModalContentProps = {
  isUpdatingSource: boolean;
  jobId: string;
  versions: FlowVersion[];
  entitySchemas: EntitySchemaResource[];
  source: JobEntitySource;
  onOpenTrafficPolicyModal: () => void;
  onSubmit: (data: EntitySourceUpdates) => void;
};

const getFlowEntitySchemaConfig = (
  entitySchemas: EntitySchemaResource[],
  versions: FlowVersion[],
) => {
  const properties = versions[0]?.input_schema?.properties || {};
  const propertyKey = Object.entries(properties).find(
    ([_, value]) => "$ref" in value && isJsonRefPath(value.$ref),
  )?.[0] as string;
  const property = propertyKey ? properties[propertyKey] : null;
  const propertyType = decodeJsonRefPath(property?.$ref ?? "");
  const entitySchema =
    entitySchemas.find(
      (entitySchema) => entitySchema._id === propertyType?.type,
    ) ?? null;

  return { entitySchema, propertyKey };
};

const ModalContent = ({
  isUpdatingSource,
  versions,
  source,
  entitySchemas,
  jobId,
  onOpenTrafficPolicyModal,
  onSubmit,
}: ModalContentProps) => {
  const versionIds = versions.map((v) => v.id);
  const polledVersions = useFlowVersions(versionIds, {
    refetchInterval: 5 * 1000,
  });

  const currentVersions = polledVersions.data || versions;
  const entitySourcePreview = useEntitySourcePreview(jobId, source.id);

  const { entitySchema, propertyKey } = getFlowEntitySchemaConfig(
    entitySchemas,
    currentVersions,
  );

  const entitySourcePreviewCount = useEntitySourcePreviewCount();
  const { featuresEventsEnabled } = useWorkspaceFeatureGates();

  const features = useFeatures({
    entityType: entitySchema?._id,
    options: { enabled: !!entitySchema },
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const maxX = containerRef.current
    ? containerRef.current.getBoundingClientRect().x +
      containerRef.current.clientWidth
    : null;

  const filteredFeatures = useMemo(() => {
    if (!featuresEventsEnabled) {
      return [];
    }

    if (!features.data) {
      return null;
    }

    return features.data.filter((feature) =>
      feature.entity_types?.includes(entitySchema?._id ?? ""),
    );
  }, [features.data, entitySchema, featuresEventsEnabled]);

  const form = useForm<FormData>({
    defaultValues: buildDefaultValues(entitySchema, source.configuration),
    mode: "onChange",
  });

  const handleDoneClick = form.handleSubmit((formValues) => {
    const orderBy = formValues.order_by ?? [];
    onSubmit({
      configuration: {
        type: EntityJobSourceConfigurationInputTypeEnum.ENTITY,
        entity_schema: entitySchema?._id ?? "",
        destination_field_name: propertyKey ?? "",
        filter_condition:
          formValues.filter_condition as EntitiesSourcePreviewRequestFilterCondition,
        order_by: orderBy.length > 0 ? orderBy : null,
        limit: formValues.limit ?? null,
      },
    });
  });

  const isSchemaValid = isValidVersions(currentVersions);

  const fieldErrors = useMemo(() => {
    return parseFieldErrors(entitySourcePreview.error);
  }, [entitySourcePreview.error]);

  return (
    <>
      <Modal.Content>
        <div className="flex min-h-[calc(100vh-240px)] gap-x-5">
          <div className="flex-1">
            <div className="pb-6">
              <Accordion
                dataLoc="entity-source-accordion"
                defaultValue={[
                  "fetch-entities",
                  "filter-conditions",
                  "sort-and-limit",
                ]}
              >
                <Accordion.Item label="Fetch Entities" value="fetch-entities">
                  <div className="flex flex-col gap-y-3">
                    <p className="font-inter-normal-12px text-gray-500">
                      Load entities from Taktile based on filter conditions. You
                      can query entities using their properties (e.g., id,
                      updated_at)
                    </p>
                    <div className="rounded-lg bg-gray-50 p-4">
                      {currentVersions.length === 0 && (
                        <EmptyState
                          actionButton={{
                            label: "Select Decision Flow",
                            onClick: onOpenTrafficPolicyModal,
                            icon: faPlus,
                          }}
                          dataLoc="no-decision-flow"
                          description="Select a Decision Flow you this job to run in order to complete mapping"
                          headline="No Decision Flow selected"
                          icon={faBox}
                        />
                      )}
                      {currentVersions.length > 0 && !isSchemaValid && (
                        <EmptyState
                          actionButton={{
                            label: "Update Decision Flow Version",
                            onClick: onOpenTrafficPolicyModal,
                            icon: faPlus,
                          }}
                          description="Jobs only support flow versions with exactly one Entity field in the input schema."
                          headline="Invalid Decision Flow"
                          icon={faWarning}
                          variant="error"
                        />
                      )}
                      {isSchemaValid && entitySchema && (
                        <EntityTypeSelector
                          entitySchema={entitySchema}
                          propertyKey={propertyKey}
                        />
                      )}
                    </div>
                  </div>
                </Accordion.Item>
                <Accordion.Item
                  label="Filter Entities"
                  value="filter-conditions"
                >
                  <div ref={containerRef} className="flex flex-col gap-y-3">
                    <div className="rounded-lg bg-gray-50 p-4">
                      {entitySchema && filteredFeatures && (
                        <EditorBoundsProvider maxX={maxX}>
                          <FormProvider {...form}>
                            <FilterConditionsAccordion
                              entitySchema={entitySchema}
                              features={filteredFeatures}
                              fieldErrors={fieldErrors}
                            />
                          </FormProvider>
                        </EditorBoundsProvider>
                      )}

                      {featuresEventsEnabled &&
                        !filteredFeatures &&
                        isSchemaValid && <FilterConditionsAccordionSkeleton />}

                      {!isSchemaValid && (
                        <EmptyState
                          description="Select an Entity to start building your filter conditions"
                          headline="No Entity selected"
                          icon={faCube}
                        />
                      )}
                    </div>
                  </div>
                </Accordion.Item>
                <Accordion.Item
                  dataLoc="sort-and-limit"
                  label="Sort and limit"
                  value="sort-and-limit"
                >
                  <div className="flex flex-col gap-y-3">
                    {entitySchema && filteredFeatures && (
                      <FormProvider {...form}>
                        <SortAndLimitAccordion
                          entitySchema={entitySchema}
                          features={filteredFeatures}
                        />
                      </FormProvider>
                    )}
                    {!isSchemaValid && (
                      <div className="rounded-lg bg-gray-50 p-4">
                        <EmptyState
                          description="Select an Entity to configure sort and limit"
                          headline="No Entity selected"
                          icon={faCube}
                        />
                      </div>
                    )}
                  </div>
                </Accordion.Item>
              </Accordion>
            </div>
          </div>

          <div className="max-w-full flex-1 flex-shrink overflow-hidden rounded-lg border border-gray-200 p-6">
            {(() => {
              if (!fieldErrors && entitySourcePreview.error) {
                return (
                  <Callout variant="error">
                    {errorMessage(entitySourcePreview.error)}
                  </Callout>
                );
              }

              if (entitySourcePreview.isPending) {
                return <EntitiesPreviewSkeleton />;
              }

              if (
                entitySourcePreview.previewData &&
                entitySourcePreview.environment &&
                entitySchema &&
                filteredFeatures
              ) {
                return (
                  <EntitiesPreview
                    countDataConfig={{
                      data: entitySourcePreviewCount.countData,
                      isPending: entitySourcePreviewCount.isPending,
                    }}
                    entitySchema={entitySchema}
                    features={filteredFeatures}
                    previewDataConfig={{
                      data: entitySourcePreview.previewData,
                      environment: entitySourcePreview.environment,
                    }}
                  />
                );
              }

              return (
                <EmptyState
                  description="Define your filters, then click Preview Entities to load results here."
                  headline="No preview yet"
                  icon={faMagnifyingGlass}
                />
              );
            })()}
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          <Button
            dataLoc="entity-source-done-button"
            disabled={!form.formState.isValid}
            loading={isUpdatingSource}
            variant="primary"
            onClick={handleDoneClick}
          >
            Done
          </Button>
        }
        secondaryButton="Cancel"
      >
        <PreviewQueryButtons
          currentFormValues={form.watch()}
          destinationFieldName={propertyKey}
          entitySchema={entitySchema}
          isFormValid={form.formState.isValid}
          isPending={
            entitySourcePreview.isPending || entitySourcePreviewCount.isPending
          }
          isSchemaValid={isSchemaValid}
          triggerCount={entitySourcePreviewCount.triggerCount}
          triggerPreview={entitySourcePreview.triggerPreview}
        />
      </Modal.Footer>
    </>
  );
};

const PreviewQueryButtons = ({
  triggerPreview,
  triggerCount,
  isPending,
  entitySchema,
  destinationFieldName,
  currentFormValues,
  isSchemaValid,
  isFormValid,
}: {
  triggerPreview: (args: UseEntitySourcePreviewMutationArgs) => void;
  triggerCount: (args: UseEntitySourcePreviewCountMutationArgs) => void;
  isPending: boolean;
  entitySchema: EntitySchemaResource | null;
  destinationFieldName: string;
  currentFormValues: FormData;
  isSchemaValid: boolean;
  isFormValid: boolean;
}) => {
  const [environment, setEnvironment] = useState<DecisionEnvironment>(
    DecisionEnvironment.LIVE,
  );

  const handleRunQuery = async () => {
    if (currentFormValues.filter_condition && entitySchema) {
      const orderByArr = currentFormValues.order_by ?? [];
      const orderBy = orderByArr.length > 0 ? orderByArr : null;
      const limit = currentFormValues.limit ?? null;
      triggerPreview({
        environment,
        destinationFieldName: destinationFieldName ?? "",
        entitySchema: entitySchema._id,
        filterCondition: currentFormValues.filter_condition,
        orderBy,
        limit,
      });
      triggerCount({
        environment,
        entitySchema: entitySchema._id,
        filterCondition: currentFormValues.filter_condition,
        limit,
      });
    }
  };

  const elements = [
    { key: DecisionEnvironment.LIVE, value: "Preview Entities in Live" },
    { key: DecisionEnvironment.SANDBOX, value: "Preview Entities in Sandbox" },
  ];

  return (
    <SplitButton
      buttonDataLoc="trigger-preview-button"
      dataLoc="trigger-preview"
      disabled={!isSchemaValid || isPending || !isFormValid}
      elements={elements}
      loading={isPending}
      prefixIcon={faPlay}
      selected={environment}
      onClick={handleRunQuery}
      onSelect={(env) => setEnvironment(env as DecisionEnvironment)}
    />
  );
};
